/**
 * CampaignPerformanceView — full-page drill-down on campaign performance.
 *
 * Sections:
 *  1. Hero KPI strip (campaigns count, weighted steering, total spend/value)
 *  2. Campaign Leaderboard with expandable rows
 *  3. Per-campaign persona breakdown + creative formula mix
 *  4. AI Action Recommendations sidebar
 */
import { useEffect, useState } from 'react';
import {
  Briefcase,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Compass,
  DollarSign,
  Layers,
  Megaphone,
  Target,
  Users,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchCampaignPerformanceResponse,
  type WorkbenchCampaignRow,
  type WorkbenchCampaignVerdict,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import { EmbeddedChatPanel, type ChatSeedInput } from './EmbeddedChatPanel';
import {
  ActionRecommendations,
  COMPACT,
  CURRENCY,
  DrillDownHeader,
  KpiTile,
  NUMBER,
} from './_drilldownShared';

const VERDICT_BADGE: Record<WorkbenchCampaignVerdict, string> = {
  strong: 'bg-teal-100 text-teal-700 ring-teal-200',
  promising: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  underperforming: 'bg-amber-100 text-amber-700 ring-amber-200',
  early: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
};

const VERDICT_BAR: Record<WorkbenchCampaignVerdict, string> = {
  strong: 'bg-teal-500',
  promising: 'bg-emerald-400',
  underperforming: 'bg-amber-300',
  early: 'bg-indigo-300',
};

function HeroKpis({ data }: { data: WorkbenchCampaignPerformanceResponse }) {
  const overallRoas = data.total_spend > 0 ? data.total_value / data.total_spend : 0;
  const strongCount = data.campaigns.filter((c) => c.verdict === 'strong').length;
  const underCount = data.campaigns.filter((c) => c.verdict === 'underperforming').length;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <KpiTile
        label="Active Campaigns"
        value={String(data.campaign_count)}
        sub={`${strongCount} strong · ${underCount} underperforming`}
        icon={<Megaphone size={14} className="text-indigo-500" />}
      />
      <KpiTile
        label="Total Spend"
        value={COMPACT.format(data.total_spend)}
        sub={`across ${data.campaign_count} campaigns`}
        icon={<DollarSign size={14} className="text-slate-500" />}
      />
      <KpiTile
        label="Est. LTV Value"
        value={COMPACT.format(data.total_value)}
        sub={`${overallRoas.toFixed(2)}x weighted LTV-ROAS`}
        icon={<CircleDot size={14} className="text-teal-500" />}
        tone={overallRoas >= 1 ? 'success' : 'default'}
      />
      <KpiTile
        label="Steering Accuracy"
        value={`${data.weighted_steering_pct.toFixed(0)}%`}
        sub="spend-weighted on-target delivery"
        icon={<Target size={14} className="text-teal-500" />}
        tone={
          data.weighted_steering_pct >= 70
            ? 'success'
            : data.weighted_steering_pct >= 50
            ? 'default'
            : 'warning'
        }
      />
      <KpiTile
        label="Top Campaign Value"
        value={
          data.campaigns[0]
            ? COMPACT.format(data.campaigns[0].estimated_value)
            : '—'
        }
        sub={data.campaigns[0]?.campaign_name ?? ''}
        icon={<Compass size={14} className="text-emerald-500" />}
      />
    </div>
  );
}

function CampaignRow({
  campaign,
  rank,
  onAsk,
}: {
  campaign: WorkbenchCampaignRow;
  rank: number;
  onAsk: (s: ChatSeedInput) => void;
}) {
  const [expanded, setExpanded] = useState(rank <= 1);
  const max =
    campaign.estimated_value > 0
      ? campaign.estimated_value
      : Math.max(campaign.spend, 1);
  const barPct = Math.min((campaign.estimated_value / max) * 100 || 5, 100);

  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-white hover:bg-slate-50 transition-colors text-left"
      >
        <span
          className={clsx(
            'w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold',
            rank === 1
              ? 'bg-teal-100 text-teal-700'
              : rank === 2
              ? 'bg-slate-200 text-slate-600'
              : rank === 3
              ? 'bg-amber-100 text-amber-700'
              : 'bg-slate-100 text-slate-500',
          )}
        >
          {rank}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-[12px] font-semibold text-slate-700 truncate"
              title={campaign.campaign_name}
            >
              {campaign.campaign_name}
            </span>
            <span
              className={clsx(
                'text-[9px] font-bold rounded-full px-1.5 py-0.5 ring-1 capitalize',
                VERDICT_BADGE[campaign.verdict],
              )}
            >
              {campaign.verdict}
            </span>
            <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5 font-mono uppercase">
              {campaign.objective}
            </span>
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">
            {campaign.ad_count} ads · steering {campaign.steering_pct.toFixed(0)}% → {campaign.top_persona}
          </div>
        </div>

        <div className="hidden sm:block w-24 h-2 bg-slate-100 rounded-full overflow-hidden shrink-0">
          <div
            className={clsx('h-full rounded-full', VERDICT_BAR[campaign.verdict])}
            style={{ width: `${barPct}%` }}
          />
        </div>

        <div className="grid grid-cols-4 gap-3 shrink-0 text-right">
          <div>
            <div className="text-[9px] text-slate-400">Spend</div>
            <div className="text-[11px] font-bold text-slate-700">
              {COMPACT.format(campaign.spend)}
            </div>
          </div>
          <div>
            <div className="text-[9px] text-slate-400">CTR</div>
            <div className="text-[11px] font-bold text-slate-700">{campaign.ctr.toFixed(2)}%</div>
          </div>
          <div>
            <div className="text-[9px] text-slate-400">LTV-ROAS</div>
            <div
              className={clsx(
                'text-[11px] font-bold',
                campaign.ltv_roas >= 1.5 ? 'text-teal-600' : campaign.ltv_roas >= 0.5 ? 'text-emerald-600' : 'text-amber-600',
              )}
            >
              {campaign.ltv_roas.toFixed(2)}x
            </div>
          </div>
          <div>
            <div className="text-[9px] text-slate-400">Est. LTV</div>
            <div className="text-[11px] font-bold text-teal-700">
              {CURRENCY.format(campaign.estimated_value)}
            </div>
          </div>
        </div>

        {expanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Persona breakdown */}
          <div>
            <div className="text-[10px] font-mono text-slate-400 uppercase mb-2 flex items-center gap-1">
              <Users size={10} />
              Persona breakdown
            </div>
            {campaign.personas.length === 0 && (
              <div className="text-[11px] text-slate-400">No persona attribution data</div>
            )}
            <div className="space-y-1">
              {campaign.personas.slice(0, 5).map((p) => (
                <div
                  key={p.persona_name}
                  className="flex items-center gap-2 text-[11px] bg-white rounded-lg border border-slate-100 px-2.5 py-1.5"
                >
                  <span className="flex-1 text-slate-700 truncate" title={p.persona_name}>
                    {p.persona_name}
                  </span>
                  <span className="text-slate-400 text-[10px] shrink-0">
                    {NUMBER.format(p.visitors)} visitors
                  </span>
                  <span className="text-teal-700 font-bold shrink-0">
                    {COMPACT.format(p.estimated_value)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Formula mix */}
          <div>
            <div className="text-[10px] font-mono text-slate-400 uppercase mb-2 flex items-center gap-1">
              <Layers size={10} />
              Top creative formulas
            </div>
            {campaign.top_formulas.length === 0 && (
              <div className="text-[11px] text-slate-400">No formula tags on this campaign's ads</div>
            )}
            <div className="space-y-1">
              {campaign.top_formulas.map((f, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 text-[11px] bg-white rounded-lg border border-slate-100 px-2.5 py-1.5"
                >
                  <span className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 font-mono capitalize">
                    {f.hook_type}
                  </span>
                  <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5 font-mono capitalize">
                    {f.tone}
                  </span>
                  <span className="ml-auto text-slate-400 text-[10px] shrink-0">
                    {f.ad_count} ads
                  </span>
                  <span className="text-slate-700 font-bold shrink-0">
                    {COMPACT.format(f.spend)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 flex items-center justify-end pt-1">
            <button
              onClick={() =>
                onAsk({
                  question: `Deep dive into campaign '${campaign.campaign_name}'. Why is it ${campaign.verdict} at ${campaign.ltv_roas.toFixed(2)}x LTV-ROAS, what persona is it actually delivering, and what should we do?`,
                  chart_axis: { series: 'campaign', campaign_name: campaign.campaign_name },
                })
              }
              className="text-[11px] text-teal-600 hover:text-teal-700 font-semibold"
            >
              Open in analyst →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function CampaignPerformanceView() {
  const { clientUid } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchCampaignPerformanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [chatSeed, setChatSeed] = useState<ChatSeedInput | null>(null);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getCampaignPerformance({ clientUid })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientUid]);

  return (
    <div className="space-y-5">
      <DrillDownHeader
        breadcrumb="Performance › Campaigns"
        title="Campaign Performance & Steering"
        icon={<Briefcase size={18} className="text-indigo-500" />}
        caption="Which campaigns deliver the persona you briefed for, and which are misfiring."
      />

      {loading && !data && (
        <div className="text-sm text-slate-400 animate-pulse py-12 text-center">
          Loading campaign performance...
        </div>
      )}

      {data && (
        <>
          <HeroKpis data={data} />

          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-12 lg:col-span-8 space-y-3">
              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
                    <Briefcase size={14} className="text-indigo-500" />
                    Campaign Leaderboard
                  </h2>
                  <span className="text-[10px] text-slate-400 font-mono">
                    ranked by est. LTV value · click to expand
                  </span>
                </div>
                <div className="space-y-2">
                  {data.campaigns.map((c, i) => (
                    <CampaignRow
                      key={c.campaign_name}
                      campaign={c}
                      rank={i + 1}
                      onAsk={setChatSeed}
                    />
                  ))}
                  {data.campaigns.length === 0 && (
                    <div className="text-xs text-slate-400 text-center py-6">
                      No campaigns with $50+ spend yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
            <aside className="col-span-12 lg:col-span-4 space-y-4">
              <ActionRecommendations
                actions={data.actions}
                onActivate={setChatSeed}
                activeSeed={chatSeed}
                emptyText="No campaign actions — coverage looks balanced."
              />
              {chatSeed && (
                <EmbeddedChatPanel seed={chatSeed} onClose={() => setChatSeed(null)} />
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
